import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/Text'
import { authApi } from '@/lib/api'

type Provider = 'google' | 'microsoft' | 'linkedin'

import { getDtVite } from '@/lib/runtime-env'

const isSsoEnabled = getDtVite('DT_VITE_SSO_ENABLED') !== 'false'

// Each <svg> below sets `color: var(--social-icon-fill)` and the inner shapes
// inherit it via `fill="currentColor"`. Token is defined in globals.css :root +
// .dark, so the icon recolors with the active theme without a JS branch.
const iconStyle = { color: 'var(--social-icon-fill)' } as const

export function SocialLogin() {
  const { t } = useTranslation('auth')
  const [loadingProvider, setLoadingProvider] = useState<Provider | null>(null)
  const [error, setError] = useState('')

  if (!isSsoEnabled) return null

  const handleOAuthSignIn = async (provider: Provider) => {
    setLoadingProvider(provider)
    setError('')
    try {
      const codeParam = new URLSearchParams(window.location.search).get('code')
      if (codeParam) {
        sessionStorage.setItem('pending_access_code', codeParam)
      }

      const fetchers = {
        google: authApi.getGoogleAuthUrl,
        microsoft: authApi.getAzureAuthUrl,
        linkedin: authApi.getLinkedinAuthUrl,
      }
      const { authorizationUrl } = await fetchers[provider]()
      window.location.href = authorizationUrl
    } catch (err: any) {
      const msg = err.response?.data?.message || t('social.failed', { provider })
      setError(msg)
      setLoadingProvider(null)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 text-base"
          onClick={() => handleOAuthSignIn('google')}
          disabled={loadingProvider !== null}
        >
          {loadingProvider === 'google' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <svg className="size-4" viewBox="0 0 24 24" style={iconStyle}>
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          )}
          {t('social.google')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 text-base"
          onClick={() => handleOAuthSignIn('microsoft')}
          disabled={loadingProvider !== null}
        >
          {loadingProvider === 'microsoft' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <svg className="size-4" viewBox="0 0 21 21" style={iconStyle}>
              <rect x="1" y="1" width="9" height="9" fill="currentColor" />
              <rect x="11" y="1" width="9" height="9" fill="currentColor" />
              <rect x="1" y="11" width="9" height="9" fill="currentColor" />
              <rect x="11" y="11" width="9" height="9" fill="currentColor" />
            </svg>
          )}
          {t('social.microsoft')}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 text-base"
          onClick={() => handleOAuthSignIn('linkedin')}
          disabled={loadingProvider !== null}
        >
          {loadingProvider === 'linkedin' ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <svg className="size-4" viewBox="0 0 24 24" style={iconStyle}>
              <path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          )}
          {t('social.linkedin')}
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="relative flex items-center justify-center">
        <Separator className="flex-1" />
        <Text as="span" size="sm" color="muted" className="px-4 uppercase tracking-widest text-xs">
          {t('or', { ns: 'common' })}
        </Text>
        <Separator className="flex-1" />
      </div>
    </div>
  )
}
